/**
 * Manual page behaviour.
 *
 * The manual's content lives in the Shopify page body, so Liquid cannot
 * introspect it. Everything here derives from the rendered DOM:
 *   - builds the table of contents from the headings that are actually present
 *   - opens ancestor <details> before honouring a deep link, so cross-references
 *     into collapsed sections resolve instead of silently failing
 *   - tracks the heading currently in view
 *
 * Without JavaScript the page loses the sidebar and keeps everything else:
 * <details> is native, so all content remains reachable.
 */
(function () {
  'use strict';

  var HEADING_SELECTOR = 'h2, h3';
  var SCROLL_GAP = 24;

  /**
   * How far down the viewport a scroll target has to land to clear the header.
   *
   * CSS scroll-margin-top covers the browser's own anchor jumps, but not the
   * window.scrollTo below, so the offset has to be computed here too. Dawn's
   * header sets --header-height on the root and its height changes with the
   * sticky mode and viewport, so read it each time rather than caching.
   */
  function scrollOffset() {
    var raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height');
    var header = parseFloat(raw);

    return (isNaN(header) ? 0 : header) + SCROLL_GAP;
  }

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[‘’“”]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  /** Give every heading a stable, unique id so the TOC and deep links can target it. */
  function ensureIds(headings) {
    var seen = Object.create(null);

    headings.forEach(function (heading) {
      var id = heading.id;

      if (!id) {
        id = slugify(heading.textContent || '') || 'section';
      }

      if (seen[id]) {
        seen[id] += 1;
        id = id + '-' + seen[id];
      } else {
        seen[id] = 1;
      }

      heading.id = id;
    });
  }

  /** Every <details> ancestor of an element, outermost last. */
  function detailsAncestors(element) {
    var found = [];
    var node = element.parentNode;

    while (node && node.nodeType === 1) {
      if (node.tagName === 'DETAILS') found.push(node);
      node = node.parentNode;
    }

    return found;
  }

  function revealTarget(target) {
    if (!target) return;

    detailsAncestors(target).forEach(function (details) {
      details.open = true;
    });
  }

  function scrollToTarget(target) {
    if (!target) return;

    var top = target.getBoundingClientRect().top + window.pageYOffset - scrollOffset();
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function buildToc(headings, nav) {
    var rootList = document.createElement('ul');
    var currentSubList = null;

    headings.forEach(function (heading) {
      var item = document.createElement('li');
      var link = document.createElement('a');
      var isSub = heading.tagName === 'H3';

      link.href = '#' + heading.id;
      link.textContent = (heading.textContent || '').trim();
      link.className = 'manual-toc__link' + (isSub ? ' manual-toc__link--h3' : '');
      link.setAttribute('data-toc-target', heading.id);
      item.appendChild(link);

      if (isSub) {
        if (!currentSubList) {
          currentSubList = document.createElement('ul');

          var lastTop = rootList.lastElementChild;
          if (lastTop) {
            lastTop.appendChild(currentSubList);
          } else {
            rootList.appendChild(currentSubList);
          }
        }
        currentSubList.appendChild(item);
      } else {
        currentSubList = null;
        rootList.appendChild(item);
      }
    });

    nav.innerHTML = '';
    nav.appendChild(rootList);
  }

  function initScrollSpy(headings, nav) {
    if (!('IntersectionObserver' in window)) return;

    var links = {};
    nav.querySelectorAll('[data-toc-target]').forEach(function (link) {
      links[link.getAttribute('data-toc-target')] = link;
    });

    var visible = new Set();

    function setActive() {
      var best = null;

      headings.forEach(function (heading) {
        if (visible.has(heading.id) && !best) best = heading.id;
      });

      Object.keys(links).forEach(function (id) {
        links[id].classList.toggle('is-active', id === best);
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visible.add(entry.target.id);
          } else {
            visible.delete(entry.target.id);
          }
        });
        setActive();
      },
      // Shrink the top of the observed area by the header, so a heading sitting
      // behind it is not counted as the one being read.
      { rootMargin: '-' + Math.round(scrollOffset()) + 'px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(function (heading) {
      observer.observe(heading);
    });
  }

  function initMobileToggle(toc) {
    var toggle = toc.querySelector('[data-toc-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', function () {
      var open = toc.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Picking a destination on mobile should close the menu behind you.
    toc.addEventListener('click', function (event) {
      if (!event.target.closest('[data-toc-target]')) return;
      if (!window.matchMedia('(max-width: 989px)').matches) return;

      toc.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /** Deep links must open whatever they land inside before scrolling. */
  function initDeepLinks(root) {
    function handleHash() {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return;

      var target;
      try {
        target = document.getElementById(decodeURIComponent(hash.slice(1)));
      } catch (error) {
        target = document.getElementById(hash.slice(1));
      }
      if (!target) return;

      revealTarget(target);
      // Wait a frame so the opened <details> has laid out before we measure.
      requestAnimationFrame(function () {
        scrollToTarget(target);
      });
    }

    root.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link) return;

      var id = link.getAttribute('href').slice(1);
      if (!id) return;

      var target = document.getElementById(id);
      if (!target) return;

      event.preventDefault();
      revealTarget(target);
      requestAnimationFrame(function () {
        scrollToTarget(target);
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', '#' + id);
        }
      });
    });

    window.addEventListener('hashchange', handleHash);
    handleHash();
  }

  /**
   * Give Shopify-hosted images a responsive srcset.
   *
   * Done here rather than in the markup so the page body stays readable for
   * whoever edits it by hand: four URLs on every one of sixty-odd images makes
   * the code view unusable. The CDN resizes on request, so this is purely a
   * matter of telling the browser which sizes exist.
   */
  function initResponsiveImages(body) {
    var WIDTHS = [400, 800, 1200, 1600];

    body.querySelectorAll('img').forEach(function (img) {
      var src = img.getAttribute('src') || '';

      if (img.hasAttribute('srcset')) return;
      if (src.indexOf('cdn.shopify.com') === -1) return;
      if (src.indexOf('?') !== -1) return; // already carries transform params

      img.setAttribute(
        'srcset',
        WIDTHS.map(function (w) {
          return src + '?width=' + w + ' ' + w + 'w';
        }).join(', ')
      );
      img.setAttribute('sizes', '(min-width: 990px) 760px, 100vw');
    });
  }

  /** Printing should show the whole manual, not just what happens to be open. */
  function initPrint(body) {
    var reopened = [];

    window.addEventListener('beforeprint', function () {
      reopened = [];
      body.querySelectorAll('details:not([open])').forEach(function (details) {
        details.open = true;
        reopened.push(details);
      });
    });

    window.addEventListener('afterprint', function () {
      reopened.forEach(function (details) {
        details.open = false;
      });
      reopened = [];
    });
  }

  function init() {
    var root = document.querySelector('[data-manual-root]');
    if (!root) return;

    var body = root.querySelector('[data-manual-body]');
    var toc = root.querySelector('[data-manual-toc]');
    if (!body) return;

    var headings = Array.prototype.slice.call(body.querySelectorAll(HEADING_SELECTOR));

    // Ids first: the deep-link handler resolves against them on initial load.
    ensureIds(headings);

    initResponsiveImages(body);
    initPrint(body);
    initDeepLinks(root);

    if (!toc || !headings.length) return;

    var nav = toc.querySelector('[data-toc-nav]');
    if (!nav) return;

    buildToc(headings, nav);
    toc.removeAttribute('hidden');
    // Signals the stylesheet that there is now a sidebar to lay out. The CSS
    // also handles this with :has(); this covers browsers without it.
    root.classList.add('is-ready');
    initMobileToggle(toc);
    initScrollSpy(headings, nav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
